import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    AlertTriangle,
    CheckCircle2,
    ClipboardCheck,
    FileText,
    Plus,
    Signature,
    Trash2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import RecordModal from './RecordModal';
import {
    useDeleteGovernanceRecord,
    useGovernanceActions,
    useGovernanceForms,
    useGovernanceRecords,
    useGovernanceSummary,
} from '../../hooks/useApi';
import {
    Badge,
    Button,
    Card,
    ConfirmDialog,
    DataTable,
    Dropdown,
    EmptyState,
    MenuItem,
    Modal,
    PageHeader,
    Pagination,
    StatCard,
    StatCardSkeleton,
    StatusBadge,
    TableToolbar,
    Tabs,
} from '../../components/ui';

const ADMIN_ROLES = ['Manager', 'Head of Department', 'Developer'];

const RATING_VARIANT = { low: 'neutral', medium: 'info', high: 'warning', critical: 'danger' };

const formatDate = (value) =>
    value
        ? new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

const isOverdue = (row) =>
    row.status !== 'closed' && row.target_date && new Date(row.target_date) < new Date(new Date().toDateString());

const GovernanceHub = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canManage = user && ADMIN_ROLES.includes(user.role);

    const [tab, setTab] = useState('records');
    const [search, setSearch] = useState('');
    const [formFilter, setFormFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [activeForm, setActiveForm] = useState(null);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const { data: summary, isLoading: summaryLoading } = useGovernanceSummary();
    const { data: formsData } = useGovernanceForms();
    const forms = useMemo(() => formsData?.data || [], [formsData]);
    const ownerOptions = useMemo(
        () => formsData?.options?.ict_teams || [],
        [formsData]
    );

    const { data: recordsData, isLoading: recordsLoading } = useGovernanceRecords({
        search: search || undefined,
        form_code: formFilter || undefined,
        status: statusFilter || undefined,
        page,
        limit: pageSize,
    });
    const records = useMemo(() => recordsData?.data || [], [recordsData]);

    const { data: actions = [], isLoading: actionsLoading } = useGovernanceActions();
    const deleteRecord = useDeleteGovernanceRecord();

    const formName = (code) => forms.find((f) => f.code === code)?.shortName || code;

    const confirmDelete = async () => {
        try {
            await deleteRecord.mutateAsync(recordToDelete);
            setRecordToDelete(null);
        } catch {
            /* the mutation surfaces its own error toast */
        }
    };

    /* ------------------------------------------------------------- columns */
    const recordColumns = [
        {
            key: 'record_ref',
            header: 'Reference',
            render: (r) => (
                <Link
                    to={`/governance/${r.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="block group"
                >
                    <span className="block text-micro font-mono text-ink-500">{r.record_ref}</span>
                    <span className="block font-medium text-ink-900 group-hover:text-primary-600 transition-colors duration-150 truncate max-w-[280px]">
                        {r.title}
                    </span>
                </Link>
            ),
        },
        {
            key: 'form_code',
            header: 'Form',
            render: (r) => (
                <>
                    <span className="block text-ink-700 truncate max-w-[180px]">{formName(r.form_code)}</span>
                    <span className="block text-micro font-mono text-ink-500">{r.form_code}</span>
                </>
            ),
        },
        {
            key: 'rating',
            header: 'Rating',
            render: (r) =>
                r.rating ? (
                    <Badge variant={RATING_VARIANT[r.rating] || 'neutral'}>{r.rating}</Badge>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status} />,
        },
        {
            key: 'open_actions',
            header: 'Open actions',
            align: 'right',
            width: 110,
            render: (r) =>
                Number(r.open_actions) > 0 ? (
                    <Badge variant="warning">{r.open_actions}</Badge>
                ) : (
                    <span className="text-muted">—</span>
                ),
        },
        {
            key: 'target_date',
            header: 'Target',
            render: (r) => (
                <span className={`tabular whitespace-nowrap ${isOverdue(r) ? 'text-danger font-medium' : 'text-ink-600'}`}>
                    {formatDate(r.target_date)}
                </span>
            ),
        },
        {
            key: 'created_by_name',
            header: 'Raised by',
            render: (r) => <span className="text-ink-700 truncate block max-w-[130px]">{r.created_by_name || '—'}</span>,
        },
    ];

    if (canManage) {
        recordColumns.push({
            key: 'actions',
            header: '',
            align: 'right',
            width: 48,
            render: (r) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Dropdown label={`Actions for ${r.record_ref}`}>
                        <MenuItem icon={FileText} onClick={() => navigate(`/governance/${r.id}`)}>
                            Open record
                        </MenuItem>
                        <MenuItem icon={Trash2} danger onClick={() => setRecordToDelete(r.id)}>
                            Delete record
                        </MenuItem>
                    </Dropdown>
                </div>
            ),
        });
    }

    const actionColumns = [
        {
            key: 'observation',
            header: 'Gap / observation',
            render: (a) => (
                <>
                    <span className="block text-ink-900">{a.observation}</span>
                    <Link
                        to={`/governance/${a.record_id}`}
                        className="block text-micro font-mono text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                    >
                        {a.record_ref}
                    </Link>
                </>
            ),
        },
        { key: 'action', header: 'Action', render: (a) => a.action || <span className="text-muted">—</span> },
        {
            key: 'owner_name',
            header: 'Owner',
            render: (a) => a.owner_name || <span className="text-muted">Unassigned</span>,
        },
        {
            key: 'target_date',
            header: 'Target',
            render: (a) => (
                <span className={`tabular whitespace-nowrap ${isOverdue(a) ? 'text-danger font-medium' : 'text-ink-600'}`}>
                    {formatDate(a.target_date)}
                </span>
            ),
        },
        { key: 'status', header: 'Status', align: 'right', render: (a) => <StatusBadge status={a.status} /> },
    ];

    const totalPages = recordsData?.totalPages || 1;
    const total = recordsData?.total || records.length;
    const hasFilters = Boolean(search || formFilter || statusFilter);

    return (
        <>
            <PageHeader
                title="ICT Governance Hub"
                description="Controlled ICT forms, their approvals and the follow-up actions they raise."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Governance' }]}
                actions={
                    <Button variant="primary" icon={Plus} onClick={() => setIsPickerOpen(true)}>
                        New record
                    </Button>
                }
            />

            {/* Hub counters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {summaryLoading
                    ? Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
                    : [
                        {
                            label: 'Open records',
                            value: summary?.open_records ?? 0,
                            icon: FileText,
                            tone: 'primary',
                            caption: `${summary?.total_records ?? 0} recorded in total`,
                        },
                        {
                            label: 'Pending approvals',
                            value: summary?.pending_approvals ?? 0,
                            icon: Signature,
                            tone: 'info',
                            caption: 'Awaiting a decision',
                        },
                        {
                            label: 'Overdue actions',
                            value: summary?.overdue_actions ?? 0,
                            icon: AlertTriangle,
                            tone: 'danger',
                            caption:
                                (summary?.overdue_actions ?? 0) === 0
                                    ? 'Nothing past its target date'
                                    : 'Past the agreed target date',
                        },
                        {
                            label: 'High / critical risk',
                            value: summary?.high_risk ?? 0,
                            icon: ClipboardCheck,
                            tone: 'warning',
                            caption: 'Open records rated high or above',
                        },
                    ].map((s) => <StatCard key={s.label} {...s} />)}
            </div>

            <Tabs
                className="mt-4 mb-4"
                value={tab}
                onChange={setTab}
                tabs={[
                    { value: 'records', label: 'Records', count: total },
                    {
                        value: 'actions',
                        label: 'Follow-up actions',
                        count: actions.filter((a) => a.status !== 'closed').length,
                    },
                ]}
            />

            {tab === 'records' ? (
                <Card>
                    <TableToolbar
                        search={search}
                        onSearchChange={(v) => {
                            setSearch(v);
                            setPage(1);
                        }}
                        searchPlaceholder="Search reference or title…"
                        filters={
                            <>
                                <select
                                    className="input w-auto"
                                    aria-label="Filter by form"
                                    value={formFilter}
                                    onChange={(e) => {
                                        setFormFilter(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">All forms</option>
                                    {forms.map((f) => (
                                        <option key={f.code} value={f.code}>
                                            {f.code} — {f.shortName}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    className="input w-auto"
                                    aria-label="Filter by status"
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">All statuses</option>
                                    <option value="draft">Draft</option>
                                    <option value="submitted">Submitted</option>
                                    <option value="in_review">In review</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </>
                        }
                    />

                    <DataTable
                        columns={recordColumns}
                        rows={records}
                        loading={recordsLoading}
                        onRowClick={(r) => navigate(`/governance/${r.id}`)}
                        empty={
                            <EmptyState
                                icon={FileText}
                                title={hasFilters ? 'No records match these filters' : 'No governance records yet'}
                                description={
                                    hasFilters
                                        ? 'Clear the search or filters to see every record.'
                                        : 'Raise the first controlled form to start the register.'
                                }
                                action={
                                    hasFilters ? (
                                        <Button
                                            onClick={() => {
                                                setSearch('');
                                                setFormFilter('');
                                                setStatusFilter('');
                                            }}
                                        >
                                            Clear filters
                                        </Button>
                                    ) : (
                                        <Button variant="primary" icon={Plus} onClick={() => setIsPickerOpen(true)}>
                                            New record
                                        </Button>
                                    )
                                }
                            />
                        }
                    />

                    {records.length > 0 && (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            total={total}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={(n) => {
                                setPageSize(n);
                                setPage(1);
                            }}
                        />
                    )}
                </Card>
            ) : (
                <Card>
                    <DataTable
                        columns={actionColumns}
                        rows={actions}
                        loading={actionsLoading}
                        onRowClick={(a) => navigate(`/governance/${a.record_id}`)}
                        empty={
                            <EmptyState
                                icon={CheckCircle2}
                                title="No follow-up actions"
                                description="Gaps recorded on any controlled form appear here until they are closed."
                            />
                        }
                    />
                </Card>
            )}

            {/* Which form are we raising? */}
            <Modal
                isOpen={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                title="New governance record"
                subtitle="Choose the controlled form to complete."
                size="md"
            >
                <ul className="space-y-2">
                    {forms.map((f) => (
                        <li key={f.code}>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsPickerOpen(false);
                                    setActiveForm(f);
                                }}
                                className="w-full text-left rounded-md border border-[var(--border-color)] px-3.5 py-3 hover:border-[var(--border-strong)] hover:bg-[var(--bg-subtle)] transition-all duration-150"
                            >
                                <span className="flex items-center gap-2">
                                    <span className="text-micro font-mono text-ink-500">{f.code}</span>
                                    <span className="text-md font-medium text-ink-900">{f.name}</span>
                                </span>
                                <span className="block text-base text-ink-600 mt-1">{f.purpose}</span>
                            </button>
                        </li>
                    ))}
                    {forms.length === 0 && (
                        <EmptyState compact title="No forms configured" />
                    )}
                </ul>
            </Modal>

            <RecordModal
                isOpen={Boolean(activeForm)}
                onClose={() => setActiveForm(null)}
                form={activeForm}
                ownerOptions={ownerOptions}
            />

            <ConfirmDialog
                isOpen={Boolean(recordToDelete)}
                title="Delete governance record"
                message="This permanently removes the record, its approval trail and its follow-up actions. Controlled records are normally closed rather than deleted."
                confirmText="Delete record"
                variant="danger"
                isLoading={deleteRecord.isPending}
                onConfirm={confirmDelete}
                onClose={() => setRecordToDelete(null)}
            />
        </>
    );
};

export default GovernanceHub;
